import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { FiChevronLeft, FiHeart } from 'react-icons/fi';
import { MdOutlineLocalFireDepartment } from 'react-icons/md';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import Loading from '../../components/common/Loading';
import ProductGrid from './ProductGrid';
import { useAuth } from '../../context/AuthContext';
import { getListings, getCategories, getFavorites } from '../../services/api';

const RecommendationsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const { listings: initialListings, userWishlist: initialWishlist = [] } = location.state || {};
  
  const [listings, setListings] = useState(initialListings || []);
  const [categories, setCategories] = useState([]);
  const [userWishlist, setUserWishlist] = useState(initialWishlist);
  const [loading, setLoading] = useState(!initialListings);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('relevance');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: null,
    minPrice: null,
    maxPrice: null,
    condition: 'all',
  });
  
  // Fetch user's favorites if logged in and not passed via state
  useEffect(() => {
    const fetchUserFavorites = async () => {
      if (!user?.user_id) {
        setUserWishlist([]);
        return;
      }

      try {
        if (initialWishlist.length === 0) {
          const favoritesResponse = await getFavorites(user.user_id);
          if (favoritesResponse.success) {
            setUserWishlist(favoritesResponse.data || []);
          }
        }
      } catch (error) {
        // console.error('Error fetching user favorites:', error);
        setUserWishlist([]);
      }
    };

    fetchUserFavorites();
  }, [user, initialWishlist.length]);

  // Fetch listings and categories if not passed via state
  useEffect(() => {
    const fetchData = async () => {
      if (initialListings) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [listingsResponse, categoriesResponse] = await Promise.all([
          getListings({
            status: 'active',
            moderation_status: 'approved',
            limit: 100
          }),
          getCategories()
        ]);

        setListings(listingsResponse.data || listingsResponse || []);
        setCategories(categoriesResponse.data || categoriesResponse || []);
      } catch (error) {
        // console.error('Error fetching recommendations:', error);
        setListings([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [initialListings]);

  // Personalize recommendations based on user's wishlist
  const personalizedListings = useMemo(() => {
    if (!listings || listings.length === 0) return [];
    
    // Filter out user's own listings first
    const filteredListings = listings.filter(listing => {
      return !(user && listing.seller_id === user.user_id);
    });
    
    if (filteredListings.length === 0) return [];
    
    const wishlistCategories = new Set();
    const wishlistBrands = new Set();
    const wishlistPriceRange = {
      min: Infinity,
      max: 0
    };

    // Analyze user's wishlist to understand preferences
    userWishlist.forEach(item => {
      if (item.category_id) {
        wishlistCategories.add(item.category_id);
      }
      if (item.brand) {
        wishlistBrands.add(item.brand.toLowerCase());
      }
      if (item.price) {
        const price = parseFloat(item.price);
        wishlistPriceRange.min = Math.min(wishlistPriceRange.min, price);
        wishlistPriceRange.max = Math.max(wishlistPriceRange.max, price);
      }
    });

    return filteredListings.map(listing => {
      let score = 0;
      let reasons = [];
      const listingPrice = parseFloat(listing.price || 0);
      
      // 1. Wishlist category match (highest priority)
      if (wishlistCategories.has(listing.category_id)) {
        score += 5;
        reasons.push('Matches your interests');
      }
      
      // 2. Brand match
      if (listing.brand && wishlistBrands.has(listing.brand.toLowerCase())) {
        score += 4;
        reasons.push('From brands you like');
      }
      
      // 3. Price range match
      if (wishlistPriceRange.min !== Infinity && 
          listingPrice >= wishlistPriceRange.min * 0.7 && 
          listingPrice <= wishlistPriceRange.max * 1.3) {
        score += 3;
        reasons.push('In your price range');
      }
      
      // 4. Boost boosted listings
      if (listing.is_boosted) {
        score += 2;
        if (!reasons.includes('Popular')) {
          reasons.push('Popular');
        }
      }
      
      // 5. Recency scoring
      const daysAgo = Math.floor((new Date() - new Date(listing.created_at)) / (1000 * 60 * 60 * 24));
      if (daysAgo < 7) score += 2;
      if (daysAgo < 1) {
        score += 1;
        reasons.push('Just added');
      }
      
      // 6. Condition preference
      const newItemsCount = userWishlist.filter(item => item.condition === 'new').length;
      const usedItemsCount = userWishlist.filter(item => item.condition === 'used').length;
      
      if (newItemsCount > usedItemsCount * 2 && listing.condition === 'new') {
        score += 2;
        reasons.push('Matches your preference for new items');
      } else if (usedItemsCount > newItemsCount * 2 && listing.condition === 'used') {
        score += 2;
        reasons.push('Matches your preference for used items');
      }
      
      return {
        ...listing,
        recommendationScore: score,
        recommendationReason: reasons[0] || (user ? 'You might like' : 'Trending'),
        recommendationReasons: reasons,
      };
    })
    .filter(item => item.recommendationScore > 0)
    .sort((a, b) => b.recommendationScore - a.recommendationScore);
  }, [listings, userWishlist, user]);

  // Filter listings (applies additional filters)
  const filteredListings = useMemo(() => {
    return personalizedListings.filter(listing => {
      if (filters.category && listing.category_id !== filters.category) return false;
      if (filters.condition !== 'all' && listing.condition !== filters.condition) return false;
      if (filters.minPrice && parseFloat(listing.price) < filters.minPrice) return false;
      if (filters.maxPrice && parseFloat(listing.price) > filters.maxPrice) return false;
      return true;
    });
  }, [personalizedListings, filters]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const clearFilters = () => {
    setFilters({
      category: null,
      minPrice: null,
      maxPrice: null,
      condition: 'all',
    });
  };

  const hasActiveFilters = 
    filters.category !== null ||
    filters.minPrice !== null ||
    filters.maxPrice !== null ||
    filters.condition !== 'all';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f5f5f5]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Loading 
            text="Loading personalized recommendations..." 
            size="large" 
            color="#ff660e" 
          />
        </div>
      </div>
    );
  }

  // Empty state
  const renderEmptyState = () => {
    // Check if empty because user's own listings were filtered out
    const userOwnListings = listings.filter(listing => 
      user && listing.seller_id === user.user_id
    ).length;
    
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 text-center">
        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-orange-100 to-orange-50 rounded-full flex items-center justify-center">
          <FiHeart className="w-10 h-10 text-orange-500" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {user 
            ? userOwnListings > 0 
              ? 'Showing only listings from other sellers' 
              : 'No personalized recommendations yet'
            : 'Sign in for personalized recommendations'
          }
        </h3>
        <p className="text-gray-600 max-w-md mx-auto mb-6">
          {user 
            ? userOwnListings > 0
              ? `We've hidden ${userOwnListings} of your own listings.`
              : "Start browsing and add items to your favorites to get personalized recommendations."
            : "Sign in to see recommendations based on your interests and favorites."
          }
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          {!user ? (
            <Link
              to="/login"
              className="px-6 py-3 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors"
            >
              Sign In
            </Link>
          ) : (
            <Link
              to="/favorites"
              className="px-6 py-3 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors"
            >
              Go to Favorites
            </Link>
          )}
          <Link
            to="/"
            className="px-6 py-3 bg-white text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors border border-gray-200"
          >
            Start Browsing
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f5f5f5]">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Page Header */}
        <div className="mb-6 bg-white rounded-xl shadow-sm p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <FiChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                  {user ? 'Personalized Recommendations' : 'Popular Items'}
                </h1>
                <p className="text-gray-600">
                  {user 
                    ? `Curated just for you based on your activities on EcoMart`
                    : 'Trending items in your area'
                  }
                </p>
              </div>
            </div>
            
            <div className="hidden md:flex items-center space-x-3">
              <div className="text-right">
                <p className="text-sm text-gray-600">Personalization</p>
                <p className="text-lg font-bold text-orange-500">
                  {user ? 'Active' : 'Sign in'}
                </p>
              </div>
              <div className="p-3 bg-gradient-to-r from-orange-50 to-orange-100 rounded-full">
                <MdOutlineLocalFireDepartment className="w-6 h-6 text-orange-500" />
              </div>
            </div>
          </div>
          
          {/* Stats */}
          {/* <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-orange-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">Recommended Items</p>
              <p className="text-lg font-bold text-gray-900">{filteredListings.length}</p>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">Your Favorites</p>
              <p className="text-lg font-bold text-gray-900">{userWishlist.length}</p>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">Categories</p>
              <p className="text-lg font-bold text-gray-900">
                {new Set(userWishlist.map(item => item.category_id)).size}
              </p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">Match Score</p>
              <p className="text-lg font-bold text-gray-900">
                {filteredListings.length > 0 
                  ? `${Math.round(filteredListings[0]?.recommendationScore || 0)}/10` 
                  : '0/10'
                }
              </p>
            </div>
          </div> */}
          
          {/* Active Filters */}
          {hasActiveFilters && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex flex-wrap gap-2">
                {filters.category && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-orange-100 text-orange-800 border border-orange-200">
                    Category: {categories.find(c => c.id === filters.category)?.name}
                    <button
                      onClick={() => handleFilterChange({ category: null })}
                      className="ml-2 hover:text-orange-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                {filters.condition !== 'all' && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-green-100 text-green-800 border border-green-200">
                    {filters.condition === 'new' ? 'Brand New' : 'Used'}
                    <button
                      onClick={() => handleFilterChange({ condition: 'all' })}
                      className="ml-2 hover:text-green-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                {(filters.minPrice || filters.maxPrice) && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-blue-100 text-blue-800 border border-blue-200">
                    Price Range
                    <button
                      onClick={() => handleFilterChange({ minPrice: null, maxPrice: null })}
                      className="ml-2 hover:text-blue-900"
                    >
                      ×
                    </button>
                  </span>
                )}
                <button
                  onClick={clearFilters}
                  className="text-sm text-orange-500 hover:text-orange-600 font-medium"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex gap-6">
          {/* Desktop Sidebar - Filters */}
          <div className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24 h-[calc(100vh-96px)]">
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-gray-900">Filters</h3>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-orange-500 hover:text-orange-600 font-medium"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                
                {/* Category Filter */}
                <div className="mb-6">
                  <h4 className="font-medium text-gray-700 mb-3">Category</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    <button
                      onClick={() => handleFilterChange({ category: null })}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm ${
                        filters.category === null 
                          ? 'bg-orange-500 text-white' 
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      All Categories
                    </button>
                    {categories.slice(0, 10).map(category => (
                      <button
                        key={category.id}
                        onClick={() => handleFilterChange({ category: category.id })}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm flex justify-between items-center ${
                          filters.category === category.id 
                            ? 'bg-orange-100 text-orange-700' 
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        <span>{category.name}</span>
                        <span className="text-gray-500 text-xs">
                          {filteredListings.filter(l => l.category_id === category.id).length}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Condition Filter */}
                <div className="mb-6">
                  <h4 className="font-medium text-gray-700 mb-3">Condition</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {['all', 'new', 'used'].map(cond => (
                      <button
                        key={cond}
                        onClick={() => handleFilterChange({ condition: cond })}
                        className={`px-3 py-2 rounded-lg text-sm capitalize ${
                          filters.condition === cond
                            ? cond === 'all' 
                              ? 'bg-orange-500 text-white' 
                              : cond === 'new' 
                                ? 'bg-green-500 text-white' 
                                : 'bg-blue-500 text-white'
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                      >
                        {cond === 'all' ? 'All' : cond}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Price Range */}
                <div className="mb-6">
                  <h4 className="font-medium text-gray-700 mb-3">Price Range</h4>
                  <div className="space-y-3">
                    <div className="flex space-x-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={filters.minPrice || ''}
                        onChange={(e) => handleFilterChange({ minPrice: e.target.value ? Number(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                      <input
                        type="number"
                        placeholder="Max"
                        value={filters.maxPrice || ''}
                        onChange={(e) => handleFilterChange({ maxPrice: e.target.value ? Number(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                </div>

                {/* Personalization Info */}
                {user && (
                  <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border border-orange-100">
                    <h4 className="font-medium text-gray-700 mb-2">Personalization Info</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Based on {userWishlist.length} items in your favorites
                    </p>
                    <Link
                      to="/favorites"
                      className="text-sm text-orange-500 hover:text-orange-600 font-medium"
                    >
                      Manage Favorites →
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Use ProductGrid component */}
            {filteredListings.length > 0 ? (
              <ProductGrid 
                listings={filteredListings}
                viewMode={viewMode}
                sortBy={sortBy}
                onViewModeChange={setViewMode}
                onSortChange={setSortBy}
                onPageChange={() => {}}
                showControls={true}
                isSearchPage={false}
              />
            ) : (
              renderEmptyState()
            )}
          </div>
        </div>
      </div>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default RecommendationsPage;